import ModalInformation from "@/app/components/ui/Modals/Information";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useStaffClinicListAPI } from "@/app/hooks/api_hooks/clinic/useStaffClinicListAPI";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { setMappedCityId, setMappedClinicData } from "@/app_redux/reducers/slices/ui_states_slice";
import SideGridImage from "@assets/images/SelectCityAndClinicModalImage.png";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { FaCity } from "react-icons/fa6";

export interface ISelectCityAndClinicModalState {}

interface IUserCityAndClinicFormFields {
  city_id: string;
  clinic_id: string;
  clinic_name: string;
}

const SelectCityAndClinicModal: React.FC<{ className?: string }> = () => {
  const { setInvisible } = useModalActions("SELECT_CITY_AND_CLINIC_MODAL");
  const authUserDisplayName = useAppSelector((state) => state?.auth?.user?.displayName);
  const { clinics, cities, mappedCityId } = useAppSelector((state) => state.root.UIStates);
  const dispatch = useAppDispatch();
  const fetchCities = useOrgCitiesListAPI("UI");
  const fetchClinics = useStaffClinicListAPI("UI");
  const configuration = useConfiguration();

  const formHook = useForm<IUserCityAndClinicFormFields>({
    mode: "onChange",
  });

  /* Select Items Section Starts */
  const citySelectItems = useSelectMenuReducer(cities, "name", "id");
  const clinicSelectItems = useSelectMenuReducer(clinics, "display_name", "id");
  /* Select Items Section Ends */

  useEffect(() => {
    fetchCities({
      withClinics: false,
    });
  }, []);

  useEffect(() => {
    if (formHook.getValues("city_id")) {
      fetchClinics({
        cityIds: [formHook.getValues("city_id")],
        withProviders: false,
      });
      formHook.setValue("clinic_id", null);
      formHook.setValue("clinic_name", null);
    }
  }, [formHook.watch("city_id")]);

  const onSubmit = (data: IUserCityAndClinicFormFields) => {
    dispatch(
      setMappedClinicData({
        id: data.clinic_id,
        name: data.clinic_name,
      }),
    );
    dispatch(setMappedCityId(data.city_id));

    localStorage.setItem(configuration.localStorage.CITY.key, data.city_id);
    localStorage.setItem(configuration.localStorage.CLINIC.key, data.clinic_id);
    localStorage.setItem(configuration.localStorage.CLINIC_NAME.key, data.clinic_name);

    setInvisible();
  };

  return (
    <ModalInformation
      modalId="SELECT_CITY_AND_CLINIC_MODAL"
      backdropBlur={true}
      dialogClassName="sm:my-8 sm:w-full sm:max-w-4xl sm:p-6 h-[400px]"
      persistent
      contentOverflow={true}
      title={
        <div className="flex justify-start items-center gap-x-2">
          <span className="text-xl">
            <FaCity />
          </span>
          <span>Please Select Your Designated City & Clinic</span>
          {mappedCityId && (
            <div className="top-2 right-0 absolute">
              <button onClick={() => setInvisible()}>
                {" "}
                <XMarkIcon className="w-6 h-6 text-gray-700 hover:text-red-500 transition-all" />{" "}
              </button>
            </div>
          )}
        </div>
      }
    >
      <div className="grid grid-cols-5 w-full h-full">
        <div className="col-span-3">
          <div className="flex flex-col justify-center items-start gap-y-3 w-full h-full">
            <p className="text-gray-600 text-left text-xl">
              Hi, <strong>{authUserDisplayName}</strong> 👋
            </p>
            <p className="text-gray-500 text-lg">
              You need to select your designated <strong>City</strong> and <strong>Clinic</strong> to map the VMS with
              correctly.{" "}
            </p>
            <div className="mt-4">
              <form className="flex justify-start items-center gap-x-2" onSubmit={formHook.handleSubmit(onSubmit)}>
                <div className="flex justify-start items-center gap-x-2 w-full">
                  <SearchableSelectMenu
                    errorMessages={[{ message: "City is required", type: "required" }]}
                    showDropdownIcon
                    placeholder="Select City"
                    fieldError={formHook.formState.errors.city_id}
                    register={formHook.register("city_id", { required: true })}
                    showTypedErrors
                    selectItems={citySelectItems}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("city_id", item.id);
                      }
                    }}
                    defaultSelected={citySelectItems?.filter((c) => c.id === formHook.watch("city_id"))[0]}
                  />
                  <SearchableSelectMenu
                    errorMessages={[{ message: "Clinic is required", type: "required" }]}
                    showDropdownIcon
                    listZIndex={"z-50"}
                    placeholder="Select Clinic"
                    fieldError={formHook.formState.errors.clinic_id}
                    register={formHook.register("clinic_id", { required: true })}
                    showTypedErrors
                    selectItems={clinicSelectItems}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("clinic_id", item.id);
                        formHook.setValue("clinic_name", item.title);
                      }
                    }}
                    defaultSelected={clinicSelectItems?.filter((c) => c.id === formHook.watch("clinic_id"))[0]}
                  />
                </div>
                <input
                  type="submit"
                  value={"Save"}
                  className="bg-indigo-600 hover:bg-indigo-500 shadow-sm px-3 py-2 rounded-md font-semibold text-sm text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                />
              </form>
            </div>
          </div>
        </div>
        <div className="col-span-2">
          <img src={SideGridImage} alt="side_grid_image" className="w-full" />
        </div>
      </div>
    </ModalInformation>
  );
};

export default SelectCityAndClinicModal;
